import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import apiFetch from 'utils/apiFetch';
import { capitalize } from 'utils/string';
import { ROUTE_THINK_SEED_CREATION } from 'constants/urls';
import LoadingComponent from 'components/LoadingComponent';
import Agenda from 'components/Agenda';
import GoogleCalendar from 'assets/images/think/googleCalendar.png';
import Countdown from 'components/Countdown';
import Faces from 'components/Steps/Faces';
import ContentComponent from 'components/Content';
import SimpleCarousel from 'components/SimpleCarousel';
import ElementCard from 'components/Cards/ElementSimpleCard';
import ThemeTableCard from 'components/Cards/ThemeTableCard';
import { getURLByPhase } from 'utils/calls';
import UsersModal from 'components/UsersModal';
import CallIconNew from 'assets/images/callIcon.svg';
import {
  CallHall, Description, Tags, Users, Tables, Affiliations,
  Content, CalendarWrapper, AgendaWrapper, CreateTable, AddButton,
} from './styles';

function Call(props) {
  const {
    literals, app, setBreadcrumb, calls, resetAll,
    match: { params: { id } }, history, actualUser,
  } = props;
  const data = _.find(calls, { id: parseInt(id, 10) });
  // Breadcrumb
  useEffect(() => {
    const breadcrumb = [
      {
        color: '#ffffff',
        text: data ? data.name : literals.networkingHall,
      },
    ];

    setBreadcrumb(breadcrumb.slice());

    resetAll();
  }, [calls, id]);


  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(0);
  const [tags, setTags] = useState([]);
  const [people, setPeople] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [relatedNarratives, setRelatedNarratives] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const elementType = 'call_for_impact';
  const elementId = id;

  const searchPeople = () => {
    const params = {};
    if (elementType) {
      params.element_type = elementType;
    }
    if (elementId) {
      params.element_id = elementId;
    }

    setPeople([]);
    apiFetch('GET', { endPoint: 'networking-hall/people' }, params)
      .then((response) => {
        setPeople(response.data);
      });
  };

  const searchTopics = () => {
    const params = {};
    if (elementType) {
      params.element_type = elementType;
    }
    if (elementId) {
      params.element_id = elementId;
    }

    setSelectedTopic(0);
    setTopics([]);
    setTags([]);
    apiFetch('GET', { endPoint: 'networking-hall/topics' }, params)
      .then((response) => {
        const finalTags = [];
        _.forEach(response.data, (topic) => {
          if (typeof topic.related_elements !== 'undefined') {
            _.forEach(topic.related_elements, (element) => {
              if (!_.find(finalTags, { id: element.id })) {
                finalTags.push(element);
              }
            });
          }
        });
        setTags(finalTags);
        setTopics(response.data);
      });
  };

  const getRelatedNarratives = () => {
    setRelatedNarratives([]);
    const params = {};
    if (elementId) {
      params.call_for_impact = elementId;
    }
    apiFetch('GET', { endPoint: 'narratives' }, params)
      .then((response) => {
        setRelatedNarratives(response);
      });
  };

  const handleSelectUser = (userId) => {
    setSelectedUser(userId);
  };

  const removeSelectedUser = () => {
    setSelectedUser(null);
  };

  useEffect(() => {
    searchTopics();
    getRelatedNarratives();
    searchPeople();
  }, [elementType, elementId]);

  const usersModalMemo = useMemo(() => {
    if (!selectedUser) {
      return null;
    }

    const usersIds = [];
    people.forEach((tmpUser) => {
      if (tmpUser.id !== actualUser.id) {
        usersIds.push(tmpUser.id);
      }
    });

    return (
      <UsersModal
        usersIds={usersIds}
        userSelected={selectedUser}
        onEnd={removeSelectedUser}
      />
    );
  }, [selectedUser]);

  const createTable = () => {
    history.push(ROUTE_THINK_SEED_CREATION);
  };


  const printTags = () => {
    return _.map(tags, (tag) => {
      return (
        <div
          className={`tag gray ${_.find(selectedTags, selectedTag => selectedTag === tag.id) ? 'selected' : ''}`}
          key={tag.id}
          data-id={tag.id}
        >
          {tag.name}
        </div>
      );
    });
  };

  const printUsers = () => {
    if (!people.length) {
      return null;
    }

    const title = literals.peopleInAllTables;

    return (
      <div className='list'>
        <div className='header'>
          <div className='title'>{title}</div>
        </div>
        <div className='name_section'>
          <div className='number'>{people.length}</div>
          {literals.people}
        </div>
        <div className='scroll_users'>
          <Faces
            options={people}
            colorText='black'
            onClick={handleSelectUser}
            onLongPress={handleSelectUser}
          />
        </div>
      </div>
    );
  };

  const printMainElements = () => {
    if (!app.main_element) {
      return null;
    }

    const mainElements = [];
    _.forEach(topics, ({ users }) => {
      _.forEach(users, ({ elements }) => {
        if (typeof elements[app.main_element] !== 'undefined' && elements[app.main_element].length) {
          _.forEach(elements[app.main_element], (element) => {
            if (!_.find(mainElements, { id: element.id })) {
              mainElements.push(element);
            }
          });
        }
      });
    });

    if (!mainElements.length) {
      return null;
    }

    const title = `${literals.elements[app.main_element] || capitalize(app.main_element)} `;

    return (
      <Affiliations>
        <div className='tables'>
          <div className='list'>
            <div className='header'>
              <div className='name_section'>
                <div className='number'>{mainElements.length}</div>
                {title}
              </div>
            </div>
            <SimpleCarousel classesList='simple_card_element' showButtons>
              {_.map(mainElements, element => (
                <ElementCard data={element} key={element.id} classesList={['simple_card_element']} />
              ))}
            </SimpleCarousel>
          </div>
        </div>
      </Affiliations>
    );
  };

  const handleClickTable = (tableId) => {
    history.push(`/calls/${id}/theme-table/${tableId}`);
  };

  const printTables = () => {
    const chatNum = topics.length;
    if (chatNum === 0) {
      return null;
    }

    return (
      <div className='tables'>
        <div className='name_section'>
          <div className='number'>{chatNum}</div>
          {literals.tables}
        </div>
        <SimpleCarousel classesList='theme_table_card' showButtons>
          {_.map(topics, think => (<ThemeTableCard key={think.id} data={think} row onClick={handleClickTable} />))}
        </SimpleCarousel>
      </div>
    );
  };

  const printAgenda = () => {
    if (!data.meetings.length && !data.speed_datings.length) {
      return null;
    }

    return (
      <AgendaWrapper>
        <Agenda meetings={data.meetings} speedDatings={data.speed_datings} />
      </AgendaWrapper>
    );
  };

  const printContent = () => {
    return (<ContentComponent narratives={data.narratives} content={data.content} />);
  };

  const [calendar, setCalendar] = useState(false);

  const changeCalendar = () => {
    setCalendar(!calendar);
  };

  const [dateEvent, setDateEvent] = useState(null);
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);
  const calendarGoogle = () => {
    let startTime = new Date(data.time_limit * 1000).toISOString();
    let endTime = new Date((data.time_limit * 1000) + data.intro_meeting_duration * 60000).toISOString();

    startTime = startTime.replace(/-/g, '');
    startTime = startTime.replace(/:/g, '');
    startTime = startTime.replace(/\./g, '');
    endTime = endTime.replace(/-/g, '');
    endTime = endTime.replace(/:/g, '');
    endTime = endTime.replace(/\./g, '');

    let url = `http://www.google.com/calendar/event?action=TEMPLATE&text=${encodeURIComponent(data.name)}&dates=${startTime}/${endTime}&details=`;

    if (data.zoom_meeting) {
      setIsLoadingCalendar(true);
      apiFetch('GET', { endPoint: `zoom/meetings/${data.zoom_meeting}` })
        .then((response) => {
          setIsLoadingCalendar(false);
          const zoom = `<a target='_blank' href='${response.data.join_url}'>${response.data.join_url}</a>`;
          url = `${url}${encodeURIComponent(zoom)}`;
          window.open(url);
        });
    } else {
      window.open(url);
    }
  };

  // Events
  const goToCall = () => {
    history.push(getURLByPhase(data));
  };

  const goToHall = () => {
    history.push(`/calls/${id}/hall`);
  };

  const goBack = () => {
    history.push('/initiatives');
  };


  useEffect(() => {
    if (data) {
      const events = [];
      const actualTime = moment().unix();

      data.meetings.forEach((meeting) => {
        if (meeting.start_date >= actualTime) {
          events.push(meeting.start_date);
        }
      });

      data.speed_datings.forEach((speedDating) => {
        if (speedDating.start_date >= actualTime) {
          events.push(speedDating.start_date);
        }
      });

      if (events.length) {
        const date = Math.min(events);
        setDateEvent(date);
      } else {
        setDateEvent(null);
      }
    }
  }, [data]);

  if (data) {
    return (
      <CallHall>
        {usersModalMemo}
        <CreateTable>
          <AddButton onClick={createTable}>+</AddButton>
        </CreateTable>
        <div className='callInfo' url={data.image} style={{ backgroundImage: `url(${data.image})` }}>
          <div className='opacity' />
          <div className='back'>
            <i className='fa fa-arrow-left' onClick={goBack} />
          </div>
          <img src={CallIconNew} alt='' />
          <div className='data'>
            <div className='name'>{data.name}</div>
            <div className='live_event'>
              {dateEvent ? (
                <div className='date'>{`${literals.liveEvent}: ${moment(dateEvent, 'X').format('DD/MM/YYYY')}`}</div>
              ) : null}
            </div>
            {dateEvent ? (
              <div className='starting'>
                {literals.startingAt}
                <Countdown time={dateEvent} enabled />
              </div>
            ) : null}
          </div>
          <div className='buttons'>
            {data.progress === 0 && <button className='main_button' onClick={goToCall}>{literals.joinEvent}</button>}
            {data.progress > 0 && <button className='main_button' onClick={goToCall}>{literals.reView}</button>}
            <button className='sec_button' onClick={goToHall}>{literals.eventHall}</button>
          </div>
        </div>
        {calendar && (
          <div className='addToCalendarWrapper'>
            <CalendarWrapper>
              <div className='close_button'>
                <i className='far fa-times-circle close_icon' onClick={changeCalendar} />
              </div>
              <div className='title'>{literals.addToCalendar}</div>
              <div className='icons'>
                {}
                {isLoadingCalendar ? (
                  <div style={{ width: '10rem', height: '10rem' }}>
                    <LoadingComponent colorAnimation={data.color} />
                  </div>
                ) : (
                  <div className='icon' onClick={calendarGoogle}>
                    <img src={GoogleCalendar} alt='' />
                    <div className='icon_name'>Google Calendar</div>
                  </div>
                )}
              </div>
            </CalendarWrapper>
          </div>
        )}
        <div className='content_call'>
          {tags.length ? (
            <Tags>
              <div className='filters_tags'>{printTags()}</div>
            </Tags>
          ) : null}

          {data.description ? (
            <Description>
              <div className='desc'>
                {data.description}
              </div>
            </Description>
          ) : null}
          {printAgenda()}
          {topics.length ? (
            <Tables>
              {printTables()}
            </Tables>
          ) : null}
          {data.narratives.length && data.content.length ? (
            <Content>
              <div className='content_list'>
                {printContent()}
              </div>
            </Content>
          ) : null}
          {printMainElements()}
          <Users>
            <div className='users'>
              {printUsers()}
            </div>
          </Users>
          <div style={{ height: '10vh' }} />
        </div>
      </CallHall>
    );
  }
  return null;
}

Call.propTypes = {
  match: PropTypes.object.isRequired,
  calls: PropTypes.array,
  actualUser: PropTypes.object.isRequired,
};

Call.defaultProps = {
  calls: [],
};

export default Call;



// WEBPACK FOOTER //
// ./src/routes/Call/components/index.jsx